// Aufrufe an die Writer-API (gleicher Origin). Das Token kommt aus der
// OIDC-Sitzung oder aus dem Notfall-Login (auth.ts).
import type { Session } from './auth.ts';

export type Text = { de: string; en: string };
export type ProductId = 'tk-login' | 'tk-planung' | 'tk-bildungsplattform' | 'iphis' | 'formulare' | 'vertretungsplan' | 'ki';
export type Mode = 'banner' | 'readonly' | 'blocked';
export type EntryType = 'maintenance' | 'incident' | 'announcement';
export type ComponentStatus = 'operational' | 'degraded' | 'outage' | 'maintenance';
export type IncidentState = 'investigating' | 'identified' | 'monitoring' | 'resolved';

export const PRODUCTS: { id: ProductId; name: string }[] = [
  { id: 'tk-login', name: 'TK-Login' },
  { id: 'tk-planung', name: 'TK-Planung' },
  { id: 'tk-bildungsplattform', name: 'TK-Bildungsplattform' },
  { id: 'iphis', name: 'IPHIS' },
  { id: 'formulare', name: 'Formulare' },
  { id: 'vertretungsplan', name: 'Vertretungsplan' },
  { id: 'ki', name: 'KI' },
];
export const productName = (id: string) => PRODUCTS.find((p) => p.id === id)?.name ?? id;

export type Scope =
  | { kind: 'global' }
  | { kind: 'products'; products: ProductId[] }
  | { kind: 'tenant'; tenant: string; products?: ProductId[] };

export type Maintenance = {
  id: string;
  type: EntryType;
  mode: Mode;
  scope: Scope;
  starts_at: string;
  ends_at: string;
  message: Text;
  status_url?: string;
  public: boolean;
  title?: Text;
  affected_components?: ProductId[];
  internal_ref?: string;
  approval: { by: string; at: string } | null;
  created_by: string;
  created_at: string;
  updated_by: string;
  updated_at: string;
};

export type Incident = {
  id: string;
  severity: 'degraded' | 'outage';
  affected_components: ProductId[];
  started_at: string;
  resolved_at: string | null;
  title: Text;
  status: IncidentState;
  published: boolean;
  updates: { id: string; at: string; state: IncidentState; message: Text }[];
  internal_ref?: string;
  updated_by: string;
  updated_at: string;
};

export type State = {
  version: number;
  updated_at: string;
  components: Partial<Record<ProductId, { status: ComponentStatus; updated_by: string; updated_at: string }>>;
  maintenances: Record<string, Maintenance>;
  incidents: Record<string, Incident>;
};

export type Me = { id: string; name: string; auth: 'oidc' | 'breakglass' | 'machine' | 'token'; roles: string[] };
export type WriteResult = { version: number; changed: boolean; published: boolean };

export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export function createApi(session: () => Session | null, onUnauthorized: () => void) {
  async function call<T>(method: string, path: string, body?: unknown, version?: number): Promise<T> {
    const s = session();
    const headers: Record<string, string> = {};
    if (s) headers['Authorization'] = `Bearer ${s.token}`;
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (version !== undefined) headers['If-Match'] = `"${version}"`;
    const res = await fetch(path, { method, headers, ...(body === undefined ? {} : { body: JSON.stringify(body) }) });
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    if (res.status === 401 && s) onUnauthorized();
    if (!res.ok) throw new ApiError(res.status, data.error ?? `HTTP ${res.status}`);
    return data as T;
  }
  return {
    me: () => call<Me>('GET', '/api/me'),
    state: () => call<State>('GET', '/api/state'),
    putMaintenance: (id: string, body: unknown, version: number) => call<WriteResult>('PUT', `/api/maintenances/${id}`, body, version),
    endMaintenance: (id: string) => call<WriteResult>('POST', `/api/maintenances/${id}/end`),
    approveMaintenance: (id: string) => call<WriteResult>('POST', `/api/maintenances/${id}/approve`),
    deleteMaintenance: (id: string) => call<WriteResult>('DELETE', `/api/maintenances/${id}`),
    putIncident: (id: string, body: unknown, version: number) => call<WriteResult>('PUT', `/api/incidents/${id}`, body, version),
    addUpdate: (id: string, body: unknown) => call<WriteResult>('POST', `/api/incidents/${id}/updates`, body),
    resolveIncident: (id: string, message: Text) => call<WriteResult>('POST', `/api/incidents/${id}/resolve`, { message }),
    setComponent: (id: ProductId, status: ComponentStatus) => call<WriteResult>('PUT', `/api/components/${id}/status`, { status }),
    passkeys: () => call<{ id: string; label: string; created_at: string; last_used_at: string | null }[]>('GET', '/api/passkeys'),
    passkeyOptions: () => call<unknown>('POST', '/api/passkeys/register/options'),
    passkeyVerify: (label: string, response: unknown) =>
      call<{ id: string; count: number }>('POST', '/api/passkeys/register/verify', { label, response }),
    removePasskey: (id: string) => call<{ changed: boolean }>('DELETE', `/api/passkeys/${encodeURIComponent(id)}`),
    tokens: () =>
      call<{ name: string; products: ProductId[]; created_by: string; created_at: string; revoked_at: string | null }[]>(
        'GET',
        '/api/tokens',
      ),
    issueToken: (name: string, products: ProductId[]) => call<{ token: string }>('POST', '/api/tokens', { name, products }),
    revokeToken: (name: string) => call<{ changed: boolean }>('DELETE', `/api/tokens/${name}`),
  };
}

export type Api = ReturnType<typeof createApi>;

const TZ = 'Europe/Berlin';
export function fmt(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    timeZone: TZ,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
/** Wert für <input type="datetime-local"> in Berliner Zeit. */
export function toLocalInput(iso: string): string {
  return new Date(iso).toLocaleString('sv-SE', { timeZone: TZ }).replace(' ', 'T').slice(0, 16);
}
/** <input type="datetime-local"> (Browserzeit) -> UTC ohne Millisekunden. */
export function fromLocalInput(value: string): string {
  return new Date(value).toISOString().replace(/\.\d{3}Z$/, 'Z');
}
export function slug(s: string): string {
  return s
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);
}
